import React, { useState } from 'react';
import SocialAuthButtons from './SocialAuthButtons';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../hooks/useTheme';

/**
 * Sign Up Form Component
 * Implements US-1.1.1: Customer Account Creation
 * Implements US-1.1.2: Guide Account Creation
 */
export default function SignUpForm({ setAuthMode, onSuccess }) {
  const { register } = useAuth();
  const theme = useTheme();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [isGuideAccount, setIsGuideAccount] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (  _e) => {
    _e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await register({
        ...formData,
        userType: isGuideAccount ? 'guide' : 'customer'
      });
      // TODO: After email verification is implemented:
      // - Show "Check your email to verify your account" message
      // - Don't close modal immediately
      // - Display instructions to check spam folder
      // - Provide "Resend verification email" button
      // - Only close modal and log in after email is verified

      // Call onSuccess callback to close modal
      if (onSuccess) {onSuccess();}
    } catch (err) {
      setError(err.message || 'Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SocialAuthButtons />

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 ${theme.focusRing}`}
              placeholder="John"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Last Name
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 ${theme.focusRing}`}
              placeholder="Doe"
              required
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 ${theme.focusRing}`}
            placeholder="john@example.com"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Used for login and booking confirmations
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Password
          </label>
          <input
            type="password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 ${theme.focusRing}`}
            placeholder="••••••••"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            At least 8 characters with uppercase, lowercase, and number
          </p>
        </div>

        <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
          <input
            type="checkbox"
            id="guideAccount"
            checked={isGuideAccount}
            onChange={(e) => setIsGuideAccount(e.target.checked)}
            className={`w-4 h-4 text-rose-500 rounded ${theme.focusRing}`}
          />
          <label htmlFor="guideAccount" className="text-sm text-gray-700 cursor-pointer">
            I am a professional guide offering services
          </label>
        </div>

        <button
          type="submit"
          disabled={loading}
          className={`w-full ${theme.buttonPrimary} py-3 rounded-lg transition font-semibold disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {loading ? 'Creating Account...' : 'Create Account'}
        </button>
      </form>

      <div className="mt-6 text-center text-sm text-gray-600">
        Already have an account?{' '}
        <button
          type="button"
          onClick={() => setAuthMode('signin')}
          className={`${theme.textPrimary} ${theme.textHover} font-semibold`}
        >
          Log in
        </button>
      </div>

      <p className="mt-4 text-xs text-gray-500 text-center">
        By creating an account, you agree to our{' '}
        <a href="/terms" className="underline">Terms of Service</a>
        {' '}and{' '}
        <a href="/privacy" className="underline">Privacy Policy</a>
      </p>
    </>
  );
}
